/*
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import { useEffect, memo } from '@web-stories-wp/react';
import type { Page } from '@src/web-stories-wp/story-editor/src/elements/types';

/**
 * Internal dependencies
 */
import objectWithout from '../../utils/objectWithout';
import { useStory } from '../story';
import { useLutFilters } from '../lutFilters';
import { getPageLutResolutionKey } from './utils/lutPreview';

interface PageCanvasCacheValidatorProps {
  pageId: string;
  clearPageCanvasCache: (props: {
    pageId: string;
    canvas?: HTMLCanvasElement;
  }) => void;
}
function PageCanvasCacheValidator({
  pageId,
  clearPageCanvasCache,
}: PageCanvasCacheValidatorProps) {
  const page = useStory(({ state }) =>
    objectWithout(state.pages.find(({ id }) => id === pageId) as Page, [
      'animations',
    ])
  );
  const lutResolutionKey = useLutFilters(({ state }) =>
    getPageLutResolutionKey(page, state)
  );

  // Clear pageCanvas whenever a page or one of its resolved LUT assets changes.
  useEffect(() => {
    clearPageCanvasCache({ pageId: page.id as string });
  }, [page, lutResolutionKey, clearPageCanvasCache]);

  // clear pageCanvas if the page gets deleted
  useEffect(() => {
    return () => clearPageCanvasCache({ pageId });
  }, [clearPageCanvasCache, pageId]);

  return null;
}

export default memo(PageCanvasCacheValidator);
